/** Documents panel (§4.6, §3.6): create drafts, issue (locks), revise (never overwrites). */
import { useState } from 'preact/hooks';
import { createRecord, putRecord, softDelete, getRecord, snapshotNow } from '../../core/db';
import { fmtCents } from '../../core/money';
import type { DocType, DocumentRecord, InventoryItem, Job, PriceItem, Settings, TakeoffLine } from '../../core/types';
import { freezeChangeOrder, freezeProgress, freezeProposal, freezeWalkthrough, nextDocNumber, type FreezeInputs } from '../../docs/freeze';
import { clientsSig, toast } from '../state';
import { proposalDraft } from '../../ai/tasks';
import { explainAiError } from '../../ai/client';
import { listLive } from '../../core/db';
import { resolveLines } from '../../docs/compute';
import { Empty } from '../bits';

interface Props {
  job: Job;
  lines: TakeoffLine[];
  inventory: InventoryItem[];
  prices: PriceItem[];
  settings: Settings;
  documents: DocumentRecord[];
  onChanged: () => Promise<void>;
}

const TYPE_LABEL: Record<DocType, string> = {
  proposal: 'Proposal', progress_update: 'Progress update',
  change_order: 'Change order', walkthrough: 'Walk-through sheet',
};

export function DocsPanel(p: Props) {
  const [kind, setKind] = useState<DocType>('proposal');
  const [narrative, setNarrative] = useState('');
  const [summary, setSummary] = useState('');
  const [next, setNext] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmIssue, setConfirmIssue] = useState<DocumentRecord | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState<DocumentRecord | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState<string | null>(null);

  async function draftNarrative() {
    setAiBusy(true); setAiErr(null);
    try {
      const resolved = resolveLines(p.lines, p.prices);
      const allDocs = await listLive('documents');
      const priors = allDocs
        .filter((d) => d.type === 'proposal' && d.status === 'issued' && d.payload.narrative)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .map((d) => d.payload.narrative!) as string[];
      const r = await proposalDraft(
        resolved.map((l) => ({ name: l.name, label: l.label, qty: l.qty, unit: l.unit })),
        priors, p.job.title, p.job.siteAddress,
      );
      const text = [r.data.narrative, r.data.exclusions && `Exclusions: ${r.data.exclusions}`, r.data.assumptions && `Assumptions: ${r.data.assumptions}`]
        .filter(Boolean).join('\n\n');
      setNarrative(text);
    } catch (e) { setAiErr(explainAiError(e)); }
    finally { setAiBusy(false); }
  }

  const docs = p.documents.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const issuedProposals = docs.filter((d) => d.type === 'proposal' && d.status === 'issued');
  const lastIssuedProposal = issuedProposals[0];

  function inputs(): FreezeInputs {
    return {
      job: p.job, lines: p.lines, prices: p.prices, inventory: p.inventory,
      // the client record, not a string parsed out of the job's notes (defect, 2026-07-27)
      client: clientsSig.value.find((c) => c.id === p.job.clientId) ?? null,
      settings: p.settings, existingDocs: p.documents,
      todayIso: new Date().toISOString().slice(0, 10),
    };
  }

  async function createDraft() {
    const i = inputs();
    let payload;
    if (kind === 'proposal') payload = freezeProposal(i, narrative);
    else if (kind === 'walkthrough') payload = freezeWalkthrough(i);
    else if (kind === 'progress_update') payload = freezeProgress(i, { summary, next, notes });
    else {
      if (!lastIssuedProposal) { toast('Issue a proposal first — a change order amends an issued proposal'); return; }
      payload = freezeChangeOrder(i, lastIssuedProposal, notes);
    }
    const doc = await createRecord('documents', {
      jobId: p.job.id, type: kind, status: 'draft',
      number: nextDocNumber(kind, p.job.bidNumber, p.documents),
      payload,
    });
    setNarrative(''); setSummary(''); setNext(''); setNotes('');
    await p.onChanged();
    toast(`${TYPE_LABEL[kind]} ${doc.number} drafted — review it, then issue`);
  }

  async function refreshDraft(d: DocumentRecord) {
    const i = inputs();
    const payload =
      d.type === 'proposal' ? freezeProposal(i, d.payload.narrative ?? '')
        : d.type === 'walkthrough' ? freezeWalkthrough(i)
          : d.type === 'progress_update' ? freezeProgress(i, { summary: d.payload.body?.['summary'] ?? '', next: d.payload.body?.['next'] ?? '', notes: d.payload.body?.['notes'] ?? '' })
            : lastIssuedProposal ? freezeChangeOrder(i, lastIssuedProposal, d.payload.body?.['note'] ?? '') : d.payload;
    await putRecord('documents', { ...d, payload });
    await p.onChanged();
    toast(`${d.number} refreshed from the current takeoff`);
  }

  async function issue(d: DocumentRecord) {
    // A draft can be unaddressed. An issued document cannot: issuing locks it, so
    // a blank client name can only be corrected by revising and reissuing after
    // the board has already seen it. This is the guard the old notes-parsing
    // client name never had (defect, 2026-07-27).
    if (d.type !== 'walkthrough' && !d.payload.clientName?.trim()) {
      toast('This document has no client on it. Pick the client at the top of the job, then refresh the draft.');
      setConfirmIssue(null);
      return;
    }
    await putRecord('documents', { ...d, status: 'issued', issuedAt: new Date().toISOString() });
    await snapshotNow('issue');
    setConfirmIssue(null);
    await p.onChanged();
    toast(`${TYPE_LABEL[d.type]} ${d.number} issued — it is now locked`);
  }

  /**
   * Throw away a draft.
   *
   * The same gap Phil reported for jobs — "no way to delete projects that
   * populate" — was true of documents too: a mistaken draft sat in the list
   * forever with no way to clear it. Only drafts go. An issued document is the
   * record of what a board was handed, and a superseded one is the record of
   * what it replaced; neither may be removed at all.
   *
   * A discarded revision puts its original back to issued. Revising marks the
   * original superseded, so abandoning the revision without undoing that would
   * leave the job with nothing live on it.
   */
  async function discard(d: DocumentRecord) {
    setConfirmDiscard(null);
    if (d.status !== 'draft') { toast('Only a draft can be discarded. An issued document is a permanent record.'); return; }
    if (d.revisionOf) {
      const original = await getRecord('documents', d.revisionOf);
      if (original && original.status === 'superseded') {
        await putRecord('documents', { ...original, status: 'issued' });
      }
    }
    await softDelete('documents', d.id);
    await p.onChanged();
    toast(`${d.number} discarded.${d.revisionOf ? ' The document it was revising is issued again.' : ''}`);
  }

  async function revise(d: DocumentRecord) {
    const copy = await createRecord('documents', {
      jobId: p.job.id, type: d.type, status: 'draft',
      number: nextDocNumber(d.type, p.job.bidNumber, p.documents),
      revisionOf: d.id,
      payload: structuredClone(d.payload),
    });
    await putRecord('documents', { ...d, status: 'superseded' });
    await p.onChanged();
    toast(`${copy.number} drafted as a revision — ${d.number} is marked superseded, not overwritten`);
  }

  return (
    <section class="card" aria-label="Documents">
      <h2 style="margin-bottom:10px;">Documents</h2>

      <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:flex-end;margin-bottom:8px;">
        <div><label>New document</label>
          <select value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value as DocType)} aria-label="Document type" style="min-height:36px;">
            {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <button class="btn btn-small btn-primary" onClick={() => void createDraft()}>Draft {TYPE_LABEL[kind].toLowerCase()}…</button>
      </div>
      {kind === 'proposal' && (
        <div>
          <textarea placeholder="Scope-of-work narrative — write it yourself, or draft it with AI and edit before issuing"
            style="width:100%;font-size:13.5px;min-height:64px;" value={narrative}
            onInput={(e) => setNarrative((e.target as HTMLTextAreaElement).value)} aria-label="Proposal narrative" />
          <button class="btn btn-small btn-ghost" style="margin-top:4px;" disabled={aiBusy}
            onClick={() => void draftNarrative()}>{aiBusy ? 'Drafting…' : 'Draft narrative with AI (~$0.06)'}</button>
          {aiErr && <p class="small" style="color:var(--ocotillo);margin-top:4px;">{aiErr}</p>}
          <p class="small muted" style="margin-top:2px;">The draft only fills this box — nothing reaches the client document until you review it here and issue (§5.10). It never invents scope beyond the takeoff (§5.6).</p>
        </div>
      )}
      {kind === 'progress_update' && (
        <div style="display:grid;gap:6px;">
          <textarea placeholder="Completed this period…" style="min-height:48px;font-size:13.5px;" value={summary} onInput={(e) => setSummary((e.target as HTMLTextAreaElement).value)} aria-label="Completed" />
          <textarea placeholder="Coming next…" style="min-height:48px;font-size:13.5px;" value={next} onInput={(e) => setNext((e.target as HTMLTextAreaElement).value)} aria-label="Next" />
        </div>
      )}
      {kind === 'change_order' && (
        <div>
          <p class="small muted" style="margin-bottom:6px;">
            {lastIssuedProposal
              ? `Compares the takeoff as it is now against issued ${lastIssuedProposal.number} and bills only the difference.`
              : 'Needs an issued proposal to compare against — issue one first.'}
          </p>
          <textarea placeholder="Why the change (optional)" style="width:100%;min-height:44px;font-size:13.5px;" value={notes} onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)} aria-label="Change order note" />
        </div>
      )}

      {docs.length === 0 ? (
        <Empty>No documents yet. Draft a walk-through sheet for the site visit, or a proposal when the takeoff is priced.</Empty>
      ) : (
        <table class="data" style="margin-top:8px;">
          <thead><tr><th>Document</th><th>Status</th><th class="money">Total</th><th></th></tr></thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id}>
                <td>
                  <a href={`#/job/${p.job.id}/doc/${d.id}`} style="font-weight:600;">{TYPE_LABEL[d.type]}</a>
                  <div class="mono small muted">{d.number}{d.revisionOf ? ' · revision' : ''} · {d.payload.issuedDate}</div>
                </td>
                <td>
                  <span class={`chip ${d.status === 'issued' ? 'chip-ok' : d.status === 'superseded' ? 'chip-warn' : ''}`}>
                    {d.status === 'draft' ? 'Draft' : d.status === 'issued' ? 'Issued' : 'Superseded'}
                  </span>
                </td>
                <td class="money num">{d.type === 'walkthrough' ? '—' : fmtCents(d.payload.math.totalCents)}</td>
                <td style="white-space:nowrap;">
                  {d.status === 'draft' && <>
                    <button class="btn btn-small btn-ghost" onClick={() => void refreshDraft(d)} title="Re-freeze this draft from the takeoff as it is right now">Refresh</button>{' '}
                    <button class="btn btn-small btn-ghost" aria-label={`Discard draft ${d.number}`}
                      onClick={() => setConfirmDiscard(d)}>Discard</button>{' '}
                    <button class="btn btn-small btn-primary" onClick={() => setConfirmIssue(d)}>Issue…</button>
                  </>}
                  {d.status === 'issued' && <button class="btn btn-small btn-ghost" onClick={() => void revise(d)}>Revise…</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirmDiscard && (
        <div class="modal-backdrop" role="dialog" aria-modal="true" aria-label="Confirm discard">
          <div class="modal">
            <h2 style="margin-bottom:10px;">Discard draft {confirmDiscard.number}?</h2>
            <p class="small" style="margin-bottom:12px;">
              This draft has not been issued, so nobody has seen it. It comes off the list and the takeoff it was
              built from is untouched — you can draft another one at any time.
              {confirmDiscard.revisionOf ? ' The document this was revising goes back to issued.' : ''}
            </p>
            <div style="display:flex;gap:8px;justify-content:flex-end;">
              <button class="btn btn-ghost" onClick={() => setConfirmDiscard(null)}>Cancel — keep it</button>
              <button class="btn btn-primary" onClick={() => void discard(confirmDiscard)}>Discard draft</button>
            </div>
          </div>
        </div>
      )}

      {confirmIssue && (
        <div class="modal-backdrop" role="dialog" aria-modal="true" aria-label="Confirm issue">
          <div class="modal">
            <h2 style="margin-bottom:10px;">Issue {TYPE_LABEL[confirmIssue.type].toLowerCase()} {confirmIssue.number}?</h2>
            <p class="small" style="margin-bottom:12px;">
              Issuing locks this document exactly as previewed — prices, quantities, and terms are frozen even if the
              price book or takeoff changes later. Corrections after issue become a numbered revision; nothing is overwritten.
            </p>
            <div style="display:flex;gap:8px;justify-content:flex-end;">
              <button class="btn btn-ghost" onClick={() => setConfirmIssue(null)}>Cancel — keep as draft</button>
              <button class="btn btn-primary" onClick={() => void issue(confirmIssue)}>Issue document</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
